import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import { MapPanelSettings } from "./MapPanelSettings"
import { MapPanelSearch } from "./MapPanelSearch";
import { MapPanelPilotCertificateCoastalSegments } from "./MapPanelPilotCertificateCoastalSegments";
import { useMap } from "@vis.gl/react-google-maps";
import { Button } from "@progress/kendo-react-buttons";
import { X } from "phosphor-react-sc";
import { getElementById } from "../../../helpers/ElementHelpers";
import { IconColors } from "../../../../services/SystemNames";
import MapPropertiesService from "../services/MapPropertiesService";
import { IMapParamsService } from "../services/MapParamsService";
import { isNullOrEmpty } from "../../../helpers/ObjectHelpers";
import { useMapId, usePanelState, useSetPanelState, usePilotCertificateCoastalSegmentPerson } from "../hooks/useMapStore";

interface MapPanelsProps {
    showSettings?: boolean,
    showAis?: boolean,
    pilotage?: any,
    paramsService: IMapParamsService,
    pecExam?: any,
}
 
export const MapPanels = ({
    showSettings = false,
    showAis = false,
    pilotage = null,
    pecExam,
    paramsService,
}: MapPanelsProps) => {
    
    const map = useMap();

    const mapId = useMapId();
    const statePanelState = usePanelState();
    const stateSetPanelState = useSetPanelState();
    const pilotCertificateCoastalSegmentPerson = usePilotCertificateCoastalSegmentPerson();

    const panelStates = MapPropertiesService.panelStates;

    const [isAdded, setIsAdded] = useState(false);
    const contentId = `googleMapPanelsContent-${mapId}`;
    const minWindowSize = 470;

    const [isActive, setIsActive] = useState(false);
    const [panelState, setPanelState] = useState<string>(panelStates.Settings);
    const [panelTabContentHeight, setPanelTabContentHeight] = useState(0);
    const [shouldCloseOnClick, setShouldCloseOnClick] = useState(false);
    const [title, setTitle] = useState("");

    const componentRef = useRef({
        isActive: false
    });
    const { current: localRef } = componentRef;

    useEffect(() => {
        setPanelState(statePanelState!);

        localRef.isActive = false;
        if (isNullOrEmpty(statePanelState)) {
            setIsActive(false);
            localRef.isActive = false;
        } else {
            setIsActive(true);
            localRef.isActive = true;
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statePanelState]);

    useEffect(() => { 

        if (!map || !showSettings || isAdded ) return;

        const content: HTMLElement | null = getElementById(contentId);
        if (!content) return;
        map.controls[window.google.maps.ControlPosition.LEFT_CENTER].push(content);

        setIsAdded(true);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, showSettings]);

    useEffect(() => {
        calculateContentHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    useEffect(() => {
        function handleResize() {
            calculateContentHeight();
        }

        window.addEventListener("resize", handleResize);

        return (() => {
            window.removeEventListener("offline", handleResize);
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {

        const getTitle = () => {
            switch (panelState) {
                case panelStates.Search:
                    return "Søk i kart";
                case panelStates.Settings:
                    return "Innstillinger";
                case panelStates.PilotCertificateCoastalSegments:
                    if(pilotCertificateCoastalSegmentPerson?.mode !== "EDIT")
                        return pilotCertificateCoastalSegmentPerson?.fullName;
                    return "Losens områder";
                default:
                    return "";
            }
        }
        setTitle(getTitle);

    }, [panelState, pilotCertificateCoastalSegmentPerson])

    return (
        <div id={contentId} className={`google-map-panels ${localRef.isActive ? 'google-map-panels-active' : ''}`}>
            <div className="google-map-panels-menu">
                <div className="text-truncate">
                    {title}
                </div>
                <div>
                    <Button fillMode="flat" onClick={onClose}>
                        <X color={IconColors.Primary} size={20} />
                    </Button>
                </div>
            </div>
            <div className="google-map-panels-content">
                <MapPanelContent show={panelState === panelStates.Search}>
                    <MapPanelSearch
                        panelTabContentHeight={panelTabContentHeight}
                        shouldCloseOnClick={shouldCloseOnClick}
                        onClose={onClose}
                        paramsService={paramsService}
                    />
                </MapPanelContent>
                <MapPanelContent show={panelState === panelStates.Settings}>
                    <MapPanelSettings
                        pilotage={pilotage}
                        showAis={showAis}
                        panelTabContentHeight={panelTabContentHeight}
                        paramsService={paramsService}
                        pecExam={pecExam}
                    />
                </MapPanelContent>
                <MapPanelContent show={panelState === panelStates.PilotCertificateCoastalSegments}>
                    <MapPanelPilotCertificateCoastalSegments />
                </MapPanelContent>
            </div>
        </div>
    )

    function calculateContentHeight() {
        if (!localRef.isActive) return;
        const elem:HTMLElement | null = getElementById(contentId);
        if (elem) {
            setPanelTabContentHeight(elem.offsetHeight - 145);
        }

        setShouldCloseOnClick(window.innerWidth <= minWindowSize);
    }

    function onClose() {
        stateSetPanelState("");
    }
}

interface MapPanelContentProps {
    show: boolean
}

const MapPanelContent = ({ children, show }: PropsWithChildren<MapPanelContentProps>) => {

    return (
        <div style={{ display: show ? "block" : "none"}}>
            {children}
        </div>
    )
}
